import "@/App.css";
import { BrowserRouter, Routes, Route, useLocation, Navigate } from "react-router-dom";
import { Toaster } from "sonner";
import { useEffect } from "react";

import { AuthProvider, useAuth } from "@/context/AuthContext";
import ProtectedRoute from "@/components/ProtectedRoute";
import MarketingLayout from "@/components/MarketingLayout";
import ScrollToTop from "@/components/ScrollToTop";
import { captureRefCodeFromUrl } from "@/lib/referral";
import { trackPageView } from "@/lib/tracking";

import Home from "@/pages/Home";
import About from "@/pages/About";
import TheRewiredLife from "@/pages/TheRewiredLife";
import TheMenopauseInvitation from "@/pages/TheMenopauseInvitation";
import TheMenopauseInvitationLive from "@/pages/TheMenopauseInvitationLive";
import MenopauseDashboard from "@/pages/MenopauseDashboard";
import MenopauseModuleView from "@/pages/MenopauseModuleView";
import MensGuideDashboard from "@/pages/MensGuideDashboard";
import MensGuideModuleView from "@/pages/MensGuideModuleView";
import CheckoutSuccess from "@/pages/CheckoutSuccess";
import Login from "@/pages/Login";
import Register from "@/pages/Register";
import ForgotPassword from "@/pages/ForgotPassword";
import ResetPassword from "@/pages/ResetPassword";
import ChangePassword from "@/pages/ChangePassword";
import RedeemFoundingMember from "@/pages/RedeemFoundingMember";
import Dashboard from "@/pages/Dashboard";
import ModuleView from "@/pages/ModuleView";
import Waitlist from "@/pages/Waitlist";
import Contact from "@/pages/Contact";
import Podcast from "@/pages/Podcast";
import StarterKit from "@/pages/StarterKit";
import MidlifeClarityGuide from "@/pages/MidlifeClarityGuide";
import FreeResources from "@/pages/FreeResources";
import PrivateSessions from "@/pages/PrivateSessions";
import ClientIntake from "@/pages/ClientIntake";
import Insights from "@/pages/Insights";
import InsightDetail from "@/pages/InsightDetail";
import AdminInsights from "@/pages/AdminInsights";
import Admin from "@/pages/Admin";
import FAQ from "@/pages/FAQ";
import MensGuide from "@/pages/MensGuide";
import NotFound from "@/pages/NotFound";

function ReferralCapture() {
  const location = useLocation();
  useEffect(() => {
    captureRefCodeFromUrl();
  }, [location.search]);
  return null;
}

function PageViewTracker() {
  const location = useLocation();
  useEffect(() => {
    // Only track marketing/public pages, never dashboards/admin.
    const p = location.pathname;
    const excluded =
      p.startsWith("/dashboard") ||
      p.startsWith("/admin") ||
      p.startsWith("/login") ||
      p.startsWith("/register") ||
      p.startsWith("/reset-password") ||
      p.startsWith("/forgot-password") ||
      p.startsWith("/checkout/");
    if (excluded) return;
    trackPageView(p);
  }, [location.pathname]);
  return null;
}

// Force any logged-in user whose account was flagged `must_change_password`
// (e.g. admin-provisioned editor accounts) to the /change-password page before
// they can reach anything else. Allows /change-password, /login, and /logout
// through so they can complete the flow or sign out.
function ForcePasswordChangeGuard() {
  const { user } = useAuth();
  const location = useLocation();
  if (!user || !user.must_change_password) return null;
  const path = location.pathname;
  const ok =
    path === "/change-password" ||
    path === "/login" ||
    path === "/logout" ||
    path.startsWith("/_") ||
    path.startsWith("/static/");
  if (ok) return null;
  return <Navigate to="/change-password" replace state={{ next: path }} />;
}

function App() {
  return (
    <div className="App">
      <AuthProvider>
        <BrowserRouter>
          <ScrollToTop />
          <ReferralCapture />
          <PageViewTracker />
          <ForcePasswordChangeGuard />
          <Routes>
            <Route path="/" element={<MarketingLayout><Home /></MarketingLayout>} />
            <Route path="/about" element={<MarketingLayout><About /></MarketingLayout>} />
            <Route path="/the-rewired-life" element={<MarketingLayout><TheRewiredLife /></MarketingLayout>} />
            {/* Permanent redirect — legacy "Making Fabulous" URL preserves SEO equity */}
            <Route path="/making-fabulous" element={<Navigate to="/the-rewired-life" replace />} />
            <Route path="/the-menopause-invitation" element={<MarketingLayout><TheMenopauseInvitation /></MarketingLayout>} />
            <Route path="/the-menopause-invitation-live" element={<MarketingLayout focused><TheMenopauseInvitationLive /></MarketingLayout>} />
            <Route path="/private-sessions" element={<MarketingLayout><PrivateSessions /></MarketingLayout>} />
            <Route path="/private-sessions/intake" element={<MarketingLayout><ClientIntake /></MarketingLayout>} />
            <Route path="/insights" element={<MarketingLayout><Insights /></MarketingLayout>} />
            <Route path="/insights/:slug" element={<MarketingLayout><InsightDetail /></MarketingLayout>} />
            <Route path="/admin/insights" element={<AdminInsights />} />
            <Route path="/checkout/success" element={<MarketingLayout><CheckoutSuccess /></MarketingLayout>} />
            <Route path="/podcast" element={<MarketingLayout><Podcast /></MarketingLayout>} />
            <Route path="/starter-kit" element={<MarketingLayout><StarterKit /></MarketingLayout>} />
            <Route path="/midlife-clarity-guide" element={<MarketingLayout><MidlifeClarityGuide /></MarketingLayout>} />
            <Route path="/free-resources" element={<MarketingLayout><FreeResources /></MarketingLayout>} />
            <Route path="/waitlist" element={<MarketingLayout><Waitlist /></MarketingLayout>} />
            <Route path="/contact" element={<MarketingLayout><Contact /></MarketingLayout>} />
            <Route path="/faq" element={<MarketingLayout><FAQ /></MarketingLayout>} />
            <Route path="/a-mans-guide-to-her-menopause" element={<MarketingLayout><MensGuide /></MarketingLayout>} />
            <Route path="/login" element={<Login />} />
            <Route path="/register" element={<Register />} />
            <Route path="/forgot-password" element={<ForgotPassword />} />
            <Route path="/reset-password" element={<ResetPassword />} />
            <Route path="/change-password" element={<ChangePassword />} />
            <Route path="/redeem-founding-member" element={<RedeemFoundingMember />} />
            <Route
              path="/dashboard"
              element={
                <ProtectedRoute>
                  <Dashboard />
                </ProtectedRoute>
              }
            />
            <Route
              path="/dashboard/module/:moduleId"
              element={
                <ProtectedRoute requirePaid>
                  <ModuleView />
                </ProtectedRoute>
              }
            />
            <Route
              path="/dashboard/menopause"
              element={
                <ProtectedRoute requireTmi>
                  <MenopauseDashboard />
                </ProtectedRoute>
              }
            />
            <Route
              path="/dashboard/menopause/module/:moduleId"
              element={
                <ProtectedRoute requireTmi>
                  <MenopauseModuleView />
                </ProtectedRoute>
              }
            />
            <Route
              path="/dashboard/mens-guide"
              element={
                <ProtectedRoute requireMensGuide>
                  <MensGuideDashboard />
                </ProtectedRoute>
              }
            />
            <Route
              path="/dashboard/mens-guide/module/:moduleId"
              element={
                <ProtectedRoute requireMensGuide>
                  <MensGuideModuleView />
                </ProtectedRoute>
              }
            />
            <Route
              path="/admin"
              element={
                <ProtectedRoute requireAdmin>
                  <Admin />
                </ProtectedRoute>
              }
            />
            <Route path="*" element={<MarketingLayout><NotFound /></MarketingLayout>} />
          </Routes>
        </BrowserRouter>
        <Toaster
          position="bottom-right"
          toastOptions={{
            style: {
              background: "#FAF6F0",
              color: "#1A2421",
              border: "1px solid #E5DDD4",
              fontFamily: "Manrope, sans-serif",
            },
          }}
        />
      </AuthProvider>
    </div>
  );
}

export default App;
